// Sugar Alarm — cycle 435: a dead episode cannot remain the action target.
//
// ⚠ WHY THIS FILE EXISTS. On 2026-09-14 at 20:27:54Z a phone posted a snooze
// against an urgent low from 13:22 that the server had recovered at 13:57;
// the server refused it, 409 `episode_closed`. The phone had been frozen
// for 6 h 39 min. Two clearing paths were both structurally closed:
//
//   • the cycle-82 recovery teardown resets only when `socket.lastAlert`
//     ITSELF carries `recovered_at` and matches the local family — but once
//     the slot had moved to the open HIGH row, the urgent-low recovery was
//     no longer the row being observed, and the snapshot's `open_alerts`
//     excludes recovered rows entirely. The stamp it waited for could never
//     arrive.
//   • the cycle-429 cross-family re-arm refuses, correctly, to move a
//     low-family episode onto a high row (that cycle's safety-review
//     finding 3). It is not weakened here.
//
// The gap is closed from the OTHER side: the socket keeps, per member, the
// server's latest statement about every row this device has seen
// (`serverRowTruth`), and an episode whose row the server's latest
// statement says is no longer LIVE is torn down, whatever its level. The
// statements are: every alert-family frame, every row of the wake reseed's
// REST list (which names recovered rows WITH their `recovered_at` — the
// door the 20:27Z stamp could never come through), and a snapshot, whose
// low-family open set is complete and so lets a low row the snapshot does
// not list be marked closed by ABSENCE.
//
// ⚠ "LIVE" IS THE SERVER'S OWN DEFINITION OF A LIVE EPISODE, AND IT IS NOT
// THE SLOT'S RULE. A row is live while it is fired and NOT RECOVERED
// (`server/api/alert_routes.py::active_count`: "snoozed/silenced/acked
// rows still COUNT — an un-recovered episode is live; only server-authored
// recovery clears it"). A SILENCED row — Stop on a plain low or a high — is
// a live, stopped episode, and the slot/audio rule (`alertRowIsOpen`:
// fired, not recovered, not silenced) must never be used here: the first
// draft of this cycle did, and the safety reviewer showed that one Stop
// would then tear down a live episode the trigger effect immediately
// re-opened, forever. For the same reason a row's ABSENCE from a
// snapshot's `open_alerts` (which the server filters to un-silenced rows)
// is evidence only when this device's own last statement about the row
// was un-silenced; a silenced row is legitimately absent and says nothing.
//
// ⚠ THE CLIENT RECOVERS NOTHING. This clears CLIENT state (the local
// episode latch) when the server's own statements say the row is no
// longer live. It never decides an episode is over on its own: a row the
// server has not named — including a high-family row merely absent from a
// snapshot, which enumerates low-family rows only, and any row when the
// snapshot carries no `open_alerts` field at all, or a list at the
// server's cap — is left alone. Silence is not evidence.

import type { AlertLevel } from "./types";

/** One row as the server last described it to this device. */
export interface ServerRowTruth {
  /** The server's episode rule over the row's columns: fired and NOT
   *  recovered. Snoozed, silenced and acknowledged rows are LIVE. */
  live: boolean;
  /** The row was silenced (Stop) as of that statement. A silenced row is
   *  live, and its absence from a snapshot's un-silenced open set is
   *  expected rather than evidence. */
  silenced: boolean;
  level: AlertLevel;
  /** When this device received the statement (epoch ms). */
  atMs: number;
}

/** The server caps a snapshot's low-family open set (`_open_alerts_payload`,
 *  `.limit(4)`). A list AT the cap may be truncated, so absence from it is
 *  not evidence; pinned against the server source by its test. */
export const SNAPSHOT_OPEN_ALERTS_LIMIT = 4;

/** Has the server's latest statement about this row said it is no longer
 *  LIVE — i.e. recovered? Pure. `false` for a row the server has never
 *  named to this device (silence is not evidence), `false` for a row it
 *  last named live, and `false` — deliberately — for a silenced row. */
export function episodeRowClosedByServer(
  alertId: string | null | undefined,
  truth: Readonly<Record<string, ServerRowTruth>> | null | undefined,
): boolean {
  if (alertId == null || truth == null) return false;
  const t = truth[alertId];
  return t != null && t.live === false;
}
